import React, { useMemo, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { Bill, BillItem } from '../../types/bills'
import { getCurrency, getShortFormDate } from '../../helpers/stringHelpers'
import {
    getBillItemCategory,
    groupBillItemsByCategory,
    isCreditBillItem,
} from '../../helpers/billItems'
import { BillDamageDetailDialog } from './BillDamageDetailDialog'

interface BillLineItemsProps {
    bill: Bill
}

const formatPeriod = (item: BillItem) => {
    if (item.period_start && item.period_end) {
        return `${getShortFormDate(item.period_start)} – ${getShortFormDate(item.period_end)}`
    }
    return item.period_start
        ? getShortFormDate(item.period_start)
        : item.period_end
        ? getShortFormDate(item.period_end)
        : null
}

const areaLabel = (item: BillItem) => {
    const damage = item.damage
    if (!damage) {
        return null
    }
    if (damage.area_name && damage.area_label) {
        return `${damage.area_name} · ${damage.area_label}`
    }
    return damage.area_name || damage.area_label || null
}

export const BillLineItems = ({ bill }: BillLineItemsProps) => {
    const [selectedItem, setSelectedItem] = useState<BillItem | null>(null)

    const groups = useMemo(() => {
        const items = (bill.bill_items || []).filter((item) => !item.is_voided)
        return groupBillItemsByCategory(items)
    }, [bill.bill_items])

    if (groups.length === 0) {
        return null
    }

    return (
        <>
            <Typography
                variant="subtitle1"
                sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, mb: 1.25 }}
            >
                Charges &amp; Credits
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 3 }}>
                {groups.map((group) => (
                    <Box
                        key={group.category}
                        sx={{
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: 1.5,
                            overflow: 'hidden',
                            backgroundColor: 'rgba(255,255,255,0.03)',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 1.75,
                                py: 1.1,
                                backgroundColor: `${group.color}18`,
                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: group.color,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: group.color,
                                        fontWeight: 700,
                                        letterSpacing: 0.2,
                                    }}
                                >
                                    {group.label}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'rgba(255,255,255,0.45)' }}
                                >
                                    {group.items.length}{' '}
                                    {group.items.length === 1 ? 'item' : 'items'}
                                </Typography>
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    color:
                                        group.category === 'concession'
                                            ? '#66bb6a'
                                            : 'rgba(255,255,255,0.88)',
                                    fontWeight: 700,
                                }}
                            >
                                {getCurrency(group.total)}
                            </Typography>
                        </Box>
                        {group.items.map((item) => {
                            const isCredit = isCreditBillItem(item)
                            const isDamage = getBillItemCategory(item) === 'damage'
                            const meta = [formatPeriod(item), item.frequency, areaLabel(item)]
                                .filter(Boolean)
                                .join(' · ')
                            return (
                                <Box
                                    key={item.id}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        px: 1.75,
                                        py: 1.35,
                                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                                        '&:last-child': { borderBottom: 'none' },
                                        backgroundColor: isCredit
                                            ? 'rgba(46,125,50,0.12)'
                                            : 'transparent',
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'rgba(255,255,255,0.9)',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {item.description}
                                        </Typography>
                                        {meta && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'rgba(255,255,255,0.45)',
                                                    display: 'block',
                                                    mt: 0.25,
                                                }}
                                            >
                                                {meta}
                                            </Typography>
                                        )}
                                        {isDamage && item.damage && (
                                            <Button
                                                size="small"
                                                onClick={() => setSelectedItem(item)}
                                                sx={{
                                                    mt: 0.5,
                                                    px: 0,
                                                    minWidth: 0,
                                                    color: '#fb923c',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                    '&:hover': {
                                                        backgroundColor: 'transparent',
                                                        textDecoration: 'underline',
                                                    },
                                                }}
                                            >
                                                View damage details
                                            </Button>
                                        )}
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: isCredit ? '#66bb6a' : 'rgba(255,255,255,0.9)',
                                            fontWeight: 700,
                                            whiteSpace: 'nowrap',
                                            pt: 0.15,
                                        }}
                                    >
                                        {getCurrency(item.amount)}
                                    </Typography>
                                </Box>
                            )
                        })}
                    </Box>
                ))}
            </Box>
            <BillDamageDetailDialog
                open={Boolean(selectedItem)}
                item={selectedItem}
                onClose={() => setSelectedItem(null)}
            />
        </>
    )
}
